import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import moment from "moment";

export interface FetchEnquiriesParams {
  page: number;
  pageSize: number;
  sort?: string;
  order?: "asc" | "desc" | "";
  search?: string;
  filters?: any[];
}

// Fetch enquiries list
export const fetchSiteVisit = createAsyncThunk(
  "enquiry/fetchSiteVisit",
  async (
    {
      page,
      pageSize,
      search,
      sort,
      order,
      filters = [],
    }: {
      page: number;
      pageSize: number;
      search?: string;
      sort?: string;
      order?: "asc" | "desc" | "";
      filters?: any[];
    }
  ) => {
    const params = new URLSearchParams();

    // Pagination
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());

    // Search
    if (search) params.append("search", search);

    // Sorting
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    // Table filters
    if (filters.length > 0) {
      filters.forEach(({ filterField, filterValue }: { filterField: string; filterValue: any }) => {
        if (!filterField || filterValue === undefined || filterValue === null) return;

        // Date range filter
        if (filterValue.from_date || filterValue.to_date) {
          if (filterValue.from_date) params.append(`${filterField}_from`, filterValue.from_date);
          if (filterValue.to_date) params.append(`${filterField}_to`, filterValue.to_date);
          return;
        }

        // Object filter (dropdowns etc.)
        if (typeof filterValue === "object") {
          Object.entries(filterValue).forEach(([key, val]) => {
            if (val !== "" && val !== null && val !== undefined) {
              params.append(key, String(val));
            }
          });
          return;
        }

        // Primitive value filter
        params.append(filterField, String(filterValue));
      });
    }

    const response: any = await Axios.get(`${api.site_visit.list}?${params.toString()}`);

    return {
      data: response.data.data || response.data || [],
      rowCount: response.data.count || response.count || 0,
    };
  }
);

// Fetch enquiry by ID
export const fetchEnquiryById = createAsyncThunk(
  "enquiry/fetchEnquiryById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/enquiry/${id}/get`);
      // Handle response structure: response.data.data or response.data
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch enquiry"
      );
    }
  }
);

// Close enquiry
export const closeEnquiry = createAsyncThunk(
  "enquiry/closeEnquiry",
  async (enquiryId: string, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`/enquiry/${enquiryId}/close/`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to close enquiry"
      );
    }
  }
);

// Update enquiry stage
export const updateEnquiryStage = createAsyncThunk(
  "enquiry/updateEnquiryStage",
  async ({ enquiryId, stage }: { enquiryId: string; stage: string }, { rejectWithValue }) => {
    try {
      const response = await Axios.patch(`/enquiry/${enquiryId}/stage/`, { stage });
      return response.data?.data || response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to update enquiry stage"
      );
    }
  }
);

// Export site visits CSV
// Export site visits CSV (frontend API function)
export const exportSiteVisitCSV = async ({
  search,
  filters,
  sort,
  order,
  columnFilters,
  page,
  pageSize,
}: {
  search?: string;
  filters?: any[];
  sort?: string;
  order?: string;
  columnFilters: any[];
  page?: number;
  pageSize?: number;
}) => {

  const params = new URLSearchParams();

  // 🔢 Pagination (optional)
  // if (page) params.append("page", String(page));
  // if (pageSize) params.append("page_size", String(pageSize));

  // ⌕ Search
  if (search) params.append("search", search);

  // 🧭 Sorting -> backend expects "ordering"
  if (sort) {
    const orderingValue = order === "desc" ? `-${sort}` : sort;
    params.append("ordering", orderingValue);
  }

  // 🔍 Filters
  if (Array.isArray(filters) && filters.length > 0) {
    filters.forEach((filter: any) => {
      const field = filter.id;
      const value = filter.value;
      if (!value) return;

      // ✅ Date range (handle FIRST) - match backend parameter names
      if (value?.from_date || value?.to_date) {
        // Backend expects: enquiry_date_from/enquiry_date_to, site_visit_date_from/site_visit_date_to, etc.
        if (value.from_date) params.append(`${field}_from`, value.from_date);
        if (value.to_date) params.append(`${field}_to`, value.to_date);
        return;
      }

      // ✅ Dropdown objects (second)
      if (typeof value === "object") {
        Object.entries(value).forEach(([key, val]) => {
          if (val !== "" && val !== null && val !== undefined)
            params.append(key, String(val));
        });
        return;
      }

      // ✅ Simple
      params.append(field, String(value));
    });
  }

  // 🔍 Column filters
  if (Array.isArray(columnFilters) && columnFilters.length > 0) {
    columnFilters.forEach((filter: any) => {
      const field = filter.id;
      const value = filter.value;
      if (!value) return;

      // ✅ Date range (handle FIRST) - match backend parameter names
      if (value?.from_date || value?.to_date) {
        // Backend expects: enquiry_date_from/enquiry_date_to, site_visit_date_from/site_visit_date_to, etc.
        if (value.from_date) params.append(`${field}_from`, value.from_date);
        if (value.to_date) params.append(`${field}_to`, value.to_date);
        return;
      }

      // ✅ Avoid `[object Object]`
      if (typeof value === "object") {
        Object.entries(value).forEach(([key, val]) => {
          if (val !== "" && val !== null && val !== undefined)
            params.append(key, String(val));
        });
        return;
      }

      params.append(field, String(value));
    });
  }

  params.append("export", "csv");

  return await Axios.get(`/site-visit/?${params.toString()}`, {
    responseType: "blob",
  });
};


// Fetch dropdown options for enquiry filters
export const fetchEnquiryDropdowns = createAsyncThunk(
  "enquiry/fetchDropdowns",
  async (columnId: string, { rejectWithValue }) => {
    try {
      let url = "";
      
      switch (columnId) {
        case "customer_name":
          url = "/enquiry/dropdown/customer_name";
          break;
        case "staff_name":
          url = "/enquiry/dropdown/staff_name";
          break;
        case "source_of_enquiry":
          url = "/enquiry/dropdown/source_of_enquiry";
          break;
        default:
          url = `/enquiry/dropdown/${columnId}`;
      }

      const response = await Axios.get(url);
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || response.data?.[columnId] || [];

      return { columnId, data };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to fetch dropdown"
      );
    }
  }
);


// Fetch staff dropdown
export const fetchStaffDropdown = createAsyncThunk(
  "siteVisit/fetchStaffDropdown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get("/users/dropdown/staff");
      console.log(response.data, "response.data in fetchStaffDropdown");
      return response.data?.data || response.data || [];
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message || "Failed to fetch staff dropdown");
    }
  }

);
